<template>
    <div>
        <div v-bind:style="mapConfig">
            <div ref="worldMapContainer1" class="map-1"></div>
        </div>
    </div>
</template>

<script>
    import * as d3 from 'd3';
    import * as moment from 'moment';
    import _ from 'lodash';
    import * as topojson from 'topojson-client';
    import { mapGetters } from 'vuex';
    import {http, HttpService} from '@/services';
    import { CONFIG } from '@/config';
    import AuthService from '@/services/auth.service';

    export default {
        data() {
            return {
                mapCenter: {
                    lat: -15.5,
                    lng: -52.5
                },
                isReady: false,
                shapes: {
                    area: undefined,
                    innerArea: undefined,
                    stores: undefined,
                    sales: undefined
                },
                ignoredAreas: [],
                worldMap: null,
                moment: moment,
                yearSelect: '',
                dataQuery: {},
                mapConfig: {
                    width: '600px',
                    height: '240px',
                    padding: 0,
                    margin: 0,
                    overflow: 'hidden',
                    maxWidth: 500,
                    maxHeight: 500,
                    borderRight: 'rgb(255,0,0) 2px solid',
                    borderBottom: 'rgb(255,0,0) 2px solid'
                }
            };
        },

        computed: {
            ...mapGetters({
            })
        },

        methods: {
            createMap() {
                const options = {
                    center: new L.LatLng(this.mapCenter.lat, this.mapCenter.lng),
                    zoom: 9,
                    zoomControl: false,
                    attributionControl: false,
                    renderer: L.canvas()
                };

                this.worldMap = L.map(this.$refs[`worldMapContainer1`], {
                    ...options,
                    preferCanvas: true,
                    zoomDelta: 0,
                    zoomSnap: 0,
                    zoom: 5,
                    boxZoom: true,
                    doubleClickZoom: false,
                    dragging: false,
                    scrollWheelZoom: false
                });

                const output = {
                    //default: L.tileLayer(`https://api.tiles.mapbox.com/v4/neocom.9251765b/{z}/{x}/{y}.png?access_token=${L.mapbox.accessToken}`),
                    //bingRoad: new L.BingLayer(bingKey, { type: 'Road' }),
                    //bingAerial: new L.BingLayer(bingKey, { type: 'AerialWithLabels' }),
                    cartodb: L.tileLayer('http://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png', {
                        attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="http://cartodb.com/attributions">CartoDB</a>',
                        subdomains: 'abcd',
                        maxZoom: 19
                    }),
                    openstreet: L.tileLayer('http://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                        attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
                        maxZoom: 19
                    }),
                    //grayscale: L.tileLayer.grayscale('http://tile.openstreetmap.org/{z}/{x}/{y}.png', {
                    //    attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
                    //    maxZoom: 19
                    //}),
                    toner: L.tileLayer('http://stamen-tiles-{s}.a.ssl.fastly.net/toner-lite/{z}/{x}/{y}.{ext}', {
                        attribution: 'Map tiles by <a href="http://stamen.com">Stamen Design</a>, <a href="http://creativecommons.org/licenses/by/3.0">CC BY 3.0</a> &mdash; Map data &copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
                        subdomains: 'abcd',
                        minZoom: 0,
                        maxZoom: 20,
                        ext: 'png'
                    })
                };

                let layerInstance = L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
                    attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="http://cartodb.com/attributions">CartoDB</a>',
                    subdomains: 'abcd',
                    maxZoom: 19,
                    ext: 'png'
                });

                if (this.dataQuery.style === 'cartodb')
                    layerInstance = output.cartodb;

                if (this.dataQuery.style === 'toner')
                    layerInstance = output.toner;

                if (this.dataQuery.style === 'openstreetmap')
                    layerInstance = output.openstreet;

                layerInstance.on('load', (e) => {
                    this.isReady = true;
                });
                layerInstance.addTo(this.worldMap);

                var style = (weight) => (() => ({
                    fill: '#4f6882',
                    color: this.dataQuery.style ? '#4f6882' : '#03a9f4',
                    weight: weight,
                    fillOpacity: this.dataQuery.style ? 0 : 0.5
                }));

                L.geoJSON(this.shapes.area, {
                    style: style(2)
                }).addTo(this.worldMap);

                L.geoJSON(this.shapes.innerArea, {
                    style: style(2)
                }).addTo(this.worldMap);

                const areaWithZoom = {
                    type: 'FeatureCollection',
                    features: []
                };
                if (this.shapes.area.features) {
                    areaWithZoom.features = areaWithZoom.features.concat(this.shapes.area.features);
                }
                if (this.shapes.innerArea.features) {
                    areaWithZoom.features = areaWithZoom.features.concat(this.shapes.innerArea.features);
                }
                const areaWithZoomLayer = L.geoJSON(areaWithZoom, {
                    style: () => ({
                        weight: 0,
                        fillOpacity: 0,
                    })
                }).addTo(this.worldMap);

                if (this.shapes.stores && this.shapes.stores.features) {
                    // Configure stores
                    // Initialize the SVG layer
                    const svgLayer = L.svg();
                    this.worldMap.addLayer(svgLayer);
                    // We simply pick up the SVG from the map object
                    const g = d3.select(svgLayer._container).append('g');
                    const svgLayerGroup = g
                        .selectAll('image')
                        .data(this.shapes.stores.features)
                        .enter()
                        .append('image')
                        .attr('xlink:href', f => `${CONFIG.URL.NEOGIG_IMAGE}/pin_${_.deburr(f.properties.Bandeira).toLowerCase().replace(/ /g, '_')}_sm.png`)
                        .attr('width', () => 14)
                        .attr('height', () => 20);

                    const setSvgLayerPosition = () => {
                        svgLayerGroup
                            .attr('x', f => this.worldMap.latLngToLayerPoint([f.geometry.coordinates[1], f.geometry.coordinates[0]]).x - 9)
                            .attr('y', f => this.worldMap.latLngToLayerPoint([f.geometry.coordinates[1], f.geometry.coordinates[0]]).y - 24);
                    };
                    this.worldMap.on('moveend', setSvgLayerPosition);
                    setSvgLayerPosition();
                }

                if (this.dataQuery.autoFit && this.dataQuery.autoFit == '1') {
                    const bounds = areaWithZoomLayer.getBounds();
                    const height = bounds._northEast.lat - bounds._southWest.lat;
                    const width = bounds._northEast.lng - bounds._southWest.lng;
                    const aspectRatio = width / height;
                    if (aspectRatio > 1) {
                        this.mapConfig.width = '500px';
                        this.mapConfig.height = ((2 - aspectRatio) * this.mapConfig.maxHeight) + 'px';
                    } else {
                        this.mapConfig.height = '500px';
                        this.mapConfig.width = (aspectRatio * this.mapConfig.maxWidth) + 'px';
                    }

                    setTimeout(() => {
                        this.worldMap.invalidateSize();
                        this.worldMap.fitBounds(areaWithZoomLayer.getBounds(), { padding: [10, 10] });
                    }, 50);
                    return;
                }

                this.worldMap.fitBounds(areaWithZoomLayer.getBounds(), { padding: [10, 10] });
            },

            requestData() {

              try {
                const auth = new AuthService();

                auth.isAuthenticated()
                    .then(user => {
                      const service = new HttpService();
                      const neogig_api = CONFIG.URL.NEOGIG_API;

                      let shapeAreaPromise;
                      let shapeInnerAreaPromise;
                      let shapeStoresPromise;

                      const citiesFromGeocodesUrl = `${neogig_api}/api/v1/localidade/GetCities`;
                      const limitrofesUrl = `${neogig_api}/api/v1/localidade/GetLimitrofesBySeat/?geocode=${this.dataQuery.geocode}`;

                      const locationUrl = `${neogig_api}/api/v1/localidade/GetLocationByVirtualArea/?client=${this.dataQuery.idClient}&virtualArea=${this.dataQuery.virtualArea}&geocode=${this.dataQuery.geocode}`;
                      const citiesIntoVirtualAreaUrl = `${neogig_api}/api/v1/localidade/GetCitiesIntoVirtualArea/?client=${this.dataQuery.idClient}&virtualArea=${this.dataQuery.virtualArea}&geocode=${this.dataQuery.geocode}`;
                      const storesUrl = `${neogig_api}/api/v1/lojaconcessionaria/getstorebyclientandlocale/?client=${this.dataQuery.idClient}&virtualArea=${this.dataQuery.virtualArea}&geocode=${this.dataQuery.geocode}`;

                      if (this.dataQuery.virtualArea != '-1' && !this.dataQuery.geocodes) {
                        shapeAreaPromise = service.http.get(locationUrl);
                        shapeInnerAreaPromise = service.http.get(citiesIntoVirtualAreaUrl);

						if  (this.dataQuery.storesVisible == 'true') {
							shapeStoresPromise = service.http.get(storesUrl);
						} else {
							shapeStoresPromise = new Promise((resolve, reject) => resolve([]));
						}
                      } else {
                        shapeStoresPromise = new Promise((resolve, reject) => resolve([]));

                        if (this.dataQuery.virtualArea == '-1') {
                          shapeAreaPromise = new Promise((resolve, reject) => resolve([]));
                          shapeInnerAreaPromise = service.http.get(limitrofesUrl);
                        }

                        if (this.dataQuery.geocodes) {
                          shapeAreaPromise = service.http.post(citiesFromGeocodesUrl, this.dataQuery.geocode.split(','));
                          shapeInnerAreaPromise = service.http.post(citiesFromGeocodesUrl, this.dataQuery.geocodes.split(','));
                        }
                      }

                      return Promise.all([shapeAreaPromise, shapeInnerAreaPromise, shapeStoresPromise])
                          .then(res => {
                            this.shapes.area = (res[0].data ? topojson.feature(res[0].data, res[0].data.objects.output) : []);
                            this.shapes.innerArea = (res[1].data ? topojson.feature(res[1].data, res[1].data.objects.output) : []);
                            if (res[2] && res[2].data && Object.keys(res[2].data.objects).length > 0) {
                              this.shapes.stores = (res[2].data ? topojson.feature(res[2].data, res[2].data.objects.output) : []);
                            }
                            this.createMap();
                          });
                    });
              } catch (err) {
                console.error(err);
              }
            }
        },

        mounted() {
            this.dataQuery = {
                idClient: this.$route.query.idClient,
                virtualArea: this.$route.query.virtualArea,
                geocode: this.$route.query.geocode,
                geocodes: this.$route.query.geocodes,
                user: this.$route.query.user,
                apikey: this.$route.query.apikey,
                style: this.$route.query.style,
                autoFit: this.$route.query.autoFit,
				storesVisible: this.$route.query.storesVisible
            };
            this.requestData();
        },

        components: {
        }
    };
</script>

<style lang="css" scoped>
    .image {
        border-radius: 4px;
        border: 1px solid #ccc;
    }

    .map-1 {
        height: 100%;
        border: 1px solid #a1a1a1;
    }

    span {
        display: block;
        font-size: 0.75rem;
    }
</style>
